<div class="confirmation-container">
  <div *ngIf="loading" class="loading-container">
    <mat-spinner diameter="50"></mat-spinner>
    <p>Loading payment details...</p>
  </div>

  <mat-card *ngIf="error && !loading" class="error-card">
    <mat-card-content>
      <mat-icon color="warn">error</mat-icon>
      <p>{{ error }}</p>
      <button mat-raised-button color="primary" (click)="goToPayments()">
        Back to Payments
      </button>
    </mat-card-content>
  </mat-card>

  <div *ngIf="payment && !loading" class="confirmation-content">

    <!-- Success Header -->
    <!-- Header follows the real status: a cancelled/failed checkout was shown as "Successful" (QA 2026-10-04). -->
    @if (isCompleted) {
      <div class="success-header">
        <div class="success-icon">
          <mat-icon>check_circle</mat-icon>
        </div>
        <h1>Payment Successful!</h1>
        <p>Your payment has been processed successfully</p>
      </div>
    } @else {
      <div class="success-header success-header--failed">
        <div class="success-icon">
          <mat-icon>{{ isPending ? 'hourglass_top' : 'cancel' }}</mat-icon>
        </div>
        <h1>{{ statusTitle }}</h1>
        <p>{{ statusMessage }}</p>
      </div>
    }

    <!-- Confirmation Card -->
    <mat-card class="confirmation-card">
      <mat-card-content>

        <!-- Confirmation Number -->
        <div class="confirmation-section highlight">
          <div class="section-header">
            <mat-icon>confirmation_number</mat-icon>
            <h2>Confirmation Number</h2>
          </div>
          <p class="confirmation-number">{{ payment.confirmationNumber }}</p>
          <p class="hint">Please save this number for your records</p>
        </div>

        <mat-divider></mat-divider>

        <!-- Payment Details -->
        <div class="confirmation-section">
          <div class="section-header">
            <mat-icon>payment</mat-icon>
            <h2>Payment Details</h2>
          </div>
          <div class="details-grid">
            <div class="detail-item">
              <span class="label">Payment Date</span>
              <span class="value">{{ payment.paymentDate | date: 'MMMM d, y h:mm a' }}</span>
            </div>
            <div class="detail-item">
              <span class="label">Payment Method</span>
              <span class="value">{{ getPaymentMethodDisplay() }}</span>
            </div>
            <div class="detail-item">
              <span class="label">Account</span>
              <span class="value">{{ payment.accountName }} ({{ payment.customerId }})</span>
            </div>
            <div class="detail-item" *ngIf="payment.transactionId">
              <span class="label">Transaction ID</span>
              <span class="value">{{ payment.transactionId }}</span>
            </div>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Returns Paid -->
        <div class="confirmation-section">
          <div class="section-header">
            <mat-icon>receipt_long</mat-icon>
            <h2>Returns Paid</h2>
          </div>
          <div class="returns-list">
            <div *ngFor="let return of payment.returns" class="return-item">
              <div class="return-info">
                <span class="return-period">{{ return.period }}</span>
                <span class="return-type">{{ return.type }}</span>
                <span class="return-prop" *ngFor="let p of return.propertyLines">
                  {{ p.address }} <span class="return-prop-gross">Gross {{ p.grossRentalReceipts | currency }}</span>
                </span>
              </div>
              <span class="return-amount">{{ return.amountPaid | currency }}</span>
            </div>
          </div>
        </div>

        <mat-divider></mat-divider>

        <!-- Payment Summary -->
        <div class="confirmation-section">
          <div class="section-header">
            <mat-icon>account_balance_wallet</mat-icon>
            <h2>Payment Summary</h2>
          </div>
          <div class="summary-grid">
            <!-- Full money flow: every charge and allowance that affects the total -->
            <div class="summary-line summary-line--muted" *ngIf="sumTaxableReceipts > 0">
              <span>Taxable rental receipts</span>
              <span>{{ sumTaxableReceipts | currency }}</span>
            </div>
            <div class="summary-line">
              <span>Total taxes</span>
              <span>{{ sumTaxes | currency }}</span>
            </div>
            <div class="summary-line" *ngIf="sumLateFee > 0">
              <span>Late fee</span>
              <span>{{ sumLateFee | currency }}</span>
            </div>
            <div class="summary-line" *ngIf="sumLateInterest > 0">
              <span>Late interest</span>
              <span>{{ sumLateInterest | currency }}</span>
            </div>
            <div class="summary-line" *ngIf="sumAllowance > 0">
              <span>Online collection allowance</span>
              <span>&minus;{{ sumAllowance | currency }}</span>
            </div>
            <div class="summary-line">
              <span>Subtotal</span>
              <span>{{ payment.totalAmountDue | currency }}</span>
            </div>
            <div class="summary-line" *ngIf="payment.processingFee > 0">
              <span>Processing fee (retained by payment processor)</span>
              <span>{{ payment.processingFee | currency }}</span>
            </div>
            <div class="summary-line total">
              <span><strong>Total Charged</strong></span>
              <span><strong>{{ payment.totalPaid | currency }}</strong></span>
            </div>
          </div>
        </div>

      </mat-card-content>
    </mat-card>

    <!-- Actions -->
    <div class="actions-bar">
      <button mat-stroked-button *ngIf="canPrint" (click)="print()">
        <mat-icon>print</mat-icon>
        Print
      </button>
      <button mat-stroked-button (click)="downloadReceipt()">
        <mat-icon>download</mat-icon>
        Download Receipt
      </button>
      <button mat-stroked-button (click)="emailReceipt()">
        <mat-icon>email</mat-icon>
        Email Receipt
      </button>
      <button mat-raised-button color="primary" (click)="goToPayments()">
        <mat-icon>arrow_back</mat-icon>
        Back to Payments
      </button>
    </div>

    <!-- Next Steps -->
    <mat-card class="next-steps-card">
      <mat-card-content>
        <h3>
          <mat-icon>info</mat-icon>
          What's Next?
        </h3>
        <ul>
          <li>Your payment has been applied to your account</li>
          <li>A receipt has been sent to your email address</li>
          <li>Please keep your confirmation number for your records</li>
          <li>You can view all your payments in Payment History</li>
        </ul>
      </mat-card-content>
    </mat-card>

  </div>
</div>
